<template>
  <BCard>
    <div class="flex justify-between items-center mb-2">
      <div class="text-black text-2xl font-medium mb-2">
        Missing transaction
      </div>
      <div class="flex items-center gap-[6px] border-[1px] border-[#E2E2E2] rounded-[6px] px-[12px] py-[6px] bg-[#fafafa]">
        <i class="k-card text-[18px] text-[#828282]" />
        <span class="text-[13px] text-[#333333] font-semibold">{{ cardDetail && cardDetail.name ? cardDetail.name : '-' }} ***{{ cardDetail && cardDetail.info ? cardDetail.info.card_number : '' }}</span>
        <BButton
          v-if="cardDetail && cardDetail.uuid"
          variant="flat"
          class="p-0 ml-1 w-[20px] h-[20px] flex items-center justify-center"
          @click="copyToClipboard(cardDetail.uuid)"
        >
          <img
            src="https://storage.googleapis.com/komerce/icon/ie7/copy.svg"
            alt="copy"
            class="w-[16px]"
          >
        </BButton>
      </div>
    </div>

    <div class="flex flex-wrap gap-[12px] mt-1 items-center justify-between mb-2">
      <div class="flex flex-wrap gap-[12px] items-center">
        <div class="w-[250px]">
          <b-input-group
            class="input-group-merge"
            size="md"
          >
            <b-input-group-prepend is-text>
              <feather-icon icon="SearchIcon" />
            </b-input-group-prepend>
            <b-form-input
              v-model="search"
              type="search"
              placeholder="Search transaction ID"
              debounce="500"
              @input="onChangeSearch"
            />
          </b-input-group>
        </div>
        <date-range-picker
          ref="picker"
          v-model="dateRange"
          :locale-data="locale"
          :show-ranges="false"
          :ranges="false"
          :opens="'auto'"
          control-container-class="border rounded"
        >
          <template v-slot:input="picker">
            <div
              class="d-flex btn justify-content-between align-items-center gap-5"
              style="height: 32px;"
            >
              <span
                v-if="!isDateChanged"
                class="d-flex justify-content-start text-[#A8A8A8] font-normal"
              >
                Start date &nbsp; ⇀ &nbsp; End date
              </span>
              <span
                v-else
                class="d-flex justify-content-start text-black font-medium"
              >
                {{ YEAR_MONTH_DAY(picker.startDate) }} ⇀ {{ YEAR_MONTH_DAY(picker.endDate) }}
              </span>
              <img
                src="https://storage.googleapis.com/komerce/assets/komerce-icon/Hitam/calendar.svg"
                alt="calendar"
                width="15px"
                class="d-flex justify-content-end"
              >
            </div>
          </template>
        </date-range-picker>
      </div>
    </div>

    <BOverlay
      :show="loadingTable"
      spinner-variant="primary"
      variant="light"
      blur="0"
      opacity=".5"
      rounded="sm"
    >
      <div
        id="table"
        class="mt-1"
        style="height: calc(100vh - 280px); overflow-y: scroll;"
      >
        <BTable
          :items="items"
          :fields="fields"
          show-empty
          empty-text="Tidak ada data"
          hover
          selectable
          select-mode="single"
        >
          <template #cell(date)="data">
            <div>
              {{ DAY_MONTH_YEAR(data.item.date) }}
            </div>
            <div class="text-[#828282] text-[12px]">
              {{ moment(data.item.date).format('HH:mm:ss') }}
            </div>
          </template>
          <template #cell(description)="data">
            <div>
              {{ data.item.desc }}
            </div>
          </template>

          <template #cell(transaction_id)="data">
            <div class="flex items-center gap-[4px]">
              <span class="text-nowrap">{{ data.item.transaction_id }}</span>
              <BButton
                variant="flat"
                class="p-0 w-[20px] h-[20px] flex items-center justify-center"
                @click="copyToClipboard(data.item.transaction_id)"
              >
                <img
                  src="https://storage.googleapis.com/komerce/icon/ie7/copy.svg"
                  alt="copy"
                  class="w-[16px]"
                >
              </BButton>
            </div>
          </template>

          <template #cell(amount)="data">
            <span
              :class="data.item.amount < 0 ? 'text-[#E12A2A]' : 'text-[#34A770]'"
              class="font-semibold text-nowrap"
            >
              {{ (0 > data.item.amount ? '-' : '') + IDR(Math.abs(data.item.amount || 0)) }}
            </span>
          </template>

          <template #cell(missing_issue)="data">
            <span
              class="px-[20px] py-[4px] rounded-[6px] text-[13px] font-semibold inline-block text-center w-[100px] text-nowrap capitalize flex items-center justify-center"
              :class="data.item.missing_in === 'komcards' ? 'bg-[#e6e6e6] text-secondary' : 'bg-[#E5F3FC] text-[#1875A7]'"
            >
              {{ data.item.missing_in }}
            </span>
          </template>

          <template #cell(action)="data">
            <div class="flex justify-center">
              <BButton
                v-if="!excludedDescriptions.includes(data.item.desc)"
                :id="`btn-verify-${data.index}`"
                variant="flat"
                :class="data.item.missing_in === 'monit' ? '!bg-[#ffeded] text-primary hover:bg-[#FCE3E3]' : '!bg-[#d8f5eb] text-success hover:bg-[#c1f0e0]'"
                class="hover:bg-[#FCE3E3] p-0 w-[32px] h-[32px] !flex items-center justify-center rounded-[6px]"
              >
                <feather-icon
                  :icon="data.item.missing_in === 'monit' ? 'ArrowLeftIcon' : 'ArrowRightIcon'"
                  size="16"
                />
              </BButton>
              <span v-else>-</span>

              <b-popover
                :target="`btn-verify-${data.index}`"
                triggers="click blur"
                placement="left"
                custom-class="shadow-lg border-[1px] border-[#E2E2E2] rounded-[8px]"
                :title="null"
              >
                <div class="flex gap-[8px] items-start p-1 max-w-[280px] text-left">
                  <img
                    src="https://storage.googleapis.com/komerce/assets/svg/warning-Info-circle.svg"
                    alt="warning"
                    class="w-[20px]"
                  >
                  <div class="flex flex-col">
                    <span class="text-[12px] font-bold text-black">Check Transaction</span>
                    <p class="text-[12px] text-black m-0 leading-[18px]">
                      Are you sure you want to proceed? The transaction will be verified first.
                    </p>
                    <div class="flex justify-end gap-[10px] pt-1">
                      <b-button
                        size="sm"
                        variant="outline-secondary"
                        class="px-[12px] py-[6px] rounded-[6px] bg-white text-dark text-[12px]"
                        @click="hidePopover(data.index)"
                      >
                        Cancel
                      </b-button>
                      <b-button
                        size="sm"
                        variant="primary"
                        class="px-[12px] py-[6px] rounded-[6px] text-[12px] text-white"
                        @click="forceInsert(data.item.id, data.index)"
                      >
                        Confirm
                      </b-button>
                    </div>
                  </div>
                </div>
              </b-popover>
            </div>
          </template>
        </BTable>
      </div>
    </BOverlay>

    <BRow class="mt-1">
      <BCol
        cols="12"
        class="flex flex-col md:flex-row justify-between bg-[#fafafa] items-center rounded p-1 gap-[12px] md:gap-0"
      >
        <div class="flex flex-wrap items-center justify-center md:justify-start gap-[12px]">
          <span class="text-black">{{ `Total ${totalRows} Data` }}</span>
          <div class="bg-light d-flex justify-content-center align-items-center p-50 rounded">
            <span class="text-black mr-50">List per page: </span>
            <BButton
              v-for="page in optionsPage"
              :key="page"
              class="btn-icon"
              size="sm"
              :variant="totalPerPage === page ? 'primary' : 'flat-dark'"
              @click="setPerPage(page)"
            >
              {{ page }}
            </BButton>
          </div>
        </div>

        <BPagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="totalPerPage"
          first-number
          last-number
          class="pagination-primary mb-0"
        >
          <template #prev-text>
            <feather-icon
              size="18"
              icon="ChevronLeftIcon"
            />
          </template>
          <template #next-text>
            <feather-icon
              size="18"
              icon="ChevronRightIcon"
            />
          </template>
        </BPagination>
      </BCol>
    </BRow>
  </BCard>
</template>

<script>
import {
  LABELDAY,
  LABELMONTH,
  DAY_MONTH_YEAR,
  YEAR_MONTH_DAY,
} from '@/libs/filterDate'
import { newAxiosIns } from '@/libs/axios'
import { IDR } from '@/libs/currency'
import moment from 'moment'
import _ from 'lodash'
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import { today, komcardsDate } from '@/store/helpers'
import {
  columnMissingTransactionsConfig,
  OPTIONS_PAGE,
} from './config'

export default {
  components: {
    DateRangePicker,
  },
  data() {
    return {
      IDR,
      DAY_MONTH_YEAR,
      YEAR_MONTH_DAY,
      moment,
      loadingTable: false,
      search: '',
      dateRange: {
        startDate: komcardsDate,
        endDate: today,
      },
      locale: {
        format: 'dd/mm/yyyy',
        daysOfWeek: LABELDAY,
        monthNames: LABELMONTH,
      },
      fields: columnMissingTransactionsConfig,
      items: [],
      currentPage: 1,
      optionsPage: OPTIONS_PAGE,
      totalPerPage: 25,
      totalRows: 0,
      cardId: 0,
      isDateChanged: false,
      cardDetail: null,
      excludedDescriptions: [
        'Received balance from Komcards',
        'Refund balance to Komcards',
        'Received balance from Main',
        'Refund balance to Main',
      ],
    }
  },
  watch: {
    $route() {
      this.cardId = this.$route.params.id
      this.getMissingList()
    },
    dateRange() {
      this.isDateChanged = true
      this.currentPage = 1
      this.getMissingList()
    },
    currentPage() {
      this.getMissingList()
    },
  },
  mounted() {
    this.cardId = this.$route.params.id
    this.getMissingList()
    this.getDetailCard()
  },
  methods: {
    onChangeSearch: _.debounce(function search() {
      const trimmedSearch = this.search.trim()
      this.search = trimmedSearch
      this.currentPage = 1
      this.getMissingList()
    }, 1000),
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$toast_success({ message: `${text} Copied` })
      })
    },
    setPerPage(page) {
      this.totalPerPage = page
      this.currentPage = 1
      this.getMissingList()
    },
    hidePopover(index) {
      this.$root.$emit('bv::hide::popover', `btn-verify-${index}`)
    },
    async getMissingList() {
      this.loadingTable = true
      const url = `komcards/api/v1/card/${this.cardId}/missing-transaction`
      const params = {
        start_date: moment(this.dateRange.startDate).format('YYYY-MM-DD'),
        end_date: moment(this.dateRange.endDate).format('YYYY-MM-DD'),
        search: this.search,
        page: this.currentPage,
        per_page: this.totalPerPage,
      }

      await newAxiosIns
        .get(url, { params })
        .then(res => {
          const data = res?.data?.data
          this.items = data || []
          this.totalRows = res?.data?.meta?.pagination?.total_row || 0
        })
        .catch(err => {
          this.$toast_error({ message: err.response?.data?.meta?.message || err.response?.data?.message || 'Gagal mengambil data' })
        })
        .finally(() => {
          this.loadingTable = false
        })
    },
    async getDetailCard() {
      const url = `komcards/api/v1/card/${this.cardId}/detail`
      await newAxiosIns
        .get(url)
        .then(res => {
          const data = res?.data?.data
          this.cardDetail = data
        })
        .catch(err => {
          this.$toast_error({ message: err.response?.data?.meta?.message || err.response?.data?.message || 'Gagal mengambil data' })
        })
        .finally(() => {
          this.loadingTable = false
        })
    },
    async forceInsert(trxId, index) {
      const url = `komcards/api/v1/admin/missing-transaction/${trxId}/force-insert`
      await newAxiosIns
        .post(url)
        .then(res => {
          this.$toast_success({ message: res?.data?.message || 'Successfully delete this transaction from Komcards mutation.' })
          this.getMissingList()
        })
        .catch(err => {
          this.$toast_error({ message: err.response?.data?.meta?.message || err.response?.data?.message || 'Gagal mengambil data' })
        })
        .finally(() => {
          this.hidePopover(index)
          this.getMissingList()
        })
    },
  },
}
</script>
